import { useRef, useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, UserPlus, Settings, Calendar, LogIn, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const stagger = (delay: number, expanded: boolean, direction: "down" | "scale" | "left" | "right") => {
  const hidden: Record<string, string> = {
    down: "translateY(-1rem)",
    scale: "scale(0.95)",
    left: "translateX(-2rem)",
    right: "translateX(2rem)",
  };
  return {
    opacity: expanded ? 1 : 0,
    transform: expanded ? "translate(0,0) scale(1)" : hidden[direction],
    transition: `opacity 600ms ease-out ${delay}ms, transform 600ms ease-out ${delay}ms`,
  } as React.CSSProperties;
};

export function SignupPathsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const isInViewRef = useRef(false);
  const doneRef = useRef(false);

  const tryReveal = useCallback(() => {
    if (doneRef.current) return;
    if (window.scrollY > 40 && isInViewRef.current) {
      doneRef.current = true;
      setExpanded(true);
    }
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isInViewRef.current = entry.isIntersecting;
        if (entry.isIntersecting) tryReveal();
      },
      { threshold: 0.15 }
    );
    observer.observe(el);

    const onScroll = () => {
      tryReveal();
      if (doneRef.current) {
        window.removeEventListener("scroll", onScroll);
        observer.disconnect();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [tryReveal]);

  return (
    <section ref={ref} className="bg-white dark:bg-card">
      {/* Gradient divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

      <div className="container px-3 sm:px-4 py-6 md:py-12">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <span
            style={stagger(0, expanded, "down")}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-medium mb-4"
          >
            <Sparkles className="w-3.5 h-3.5" />
            One platform, two easy ways to get started
          </span>
          <h2
            style={stagger(100, expanded, "scale")}
            className="text-2xl md:text-3xl font-bold text-foreground"
          >
            Simple for both businesses and customers
          </h2>
        </div>

        {/* Two-column cards */}
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
          {/* Business Card */}
          <div
            style={stagger(200, expanded, "left")}
            className="rounded-xl border border-border bg-card overflow-hidden transition-transform duration-300 hover:-translate-y-1 section-glow"
          >
            <div className="h-1 bg-primary" />
            <div className="p-4 sm:p-6">
              <p className="text-xs font-bold uppercase tracking-wider text-primary mb-4">
                For Businesses
              </p>
              <div className="space-y-4 mb-6">
                {[
                  { num: "1", icon: Building2, text: "Sign up as a business" },
                  { num: "2", icon: Settings, text: "Add your services & staff" },
                  { num: "3", icon: Calendar, text: "Start accepting bookings" },
                ].map((step) => (
                  <div key={step.num} className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-bold shrink-0">
                      {step.num}
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm font-medium text-foreground">
                      <step.icon className="h-4 w-4 text-primary" />
                      {step.text}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-2">
                <Button asChild className="w-full text-sm btn-glass-shine">
                  <Link to="/signup/business">
                    Create Business Account
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="w-full text-sm hover:bg-primary hover:text-primary-foreground"
                >
                  <Link to="/features">See business features</Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Customer Card */}
          <div
            style={stagger(300, expanded, "right")}
            className="rounded-xl border border-border bg-card overflow-hidden transition-transform duration-300 hover:-translate-y-1 section-glow"
          >
            <div className="h-1 bg-accent" />
            <div className="p-4 sm:p-6">
              <p className="text-xs font-bold uppercase tracking-wider text-accent mb-4">
                For Customers
              </p>
              <div className="space-y-4 mb-6">
                {[
                  { icon: UserPlus, text: "Create a free account" },
                  { icon: Building2, text: "Find your service provider" },
                  { icon: Calendar, text: "Book appointments 24/7" },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/10 text-accent text-sm font-bold shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm font-medium text-foreground">
                      <step.icon className="h-4 w-4 text-accent" />
                      {step.text}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-2">
                <Button asChild className="w-full text-sm bg-accent hover:bg-accent/90 text-white btn-glass-shine">
                  <Link to="/signup">
                    Create Customer Account
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/login">
                    <LogIn className="mr-2 h-4 w-4" />
                    Login as Customer
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
